import { useRolodexEdit } from 'features/fde'
import { usePointCloudContext } from 'features/pointClouds'
import React, { FC, useEffect, useState } from 'react'
import styled from 'styled-components'

const EditorContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 16px;
`

const JsonTextArea = styled.textarea`
  font-family: monospace;
  min-height: 150px;
  width: 300px;
  padding: 8px;
  border: 1px solid ${({ theme }) => theme.forecast.border};
  border-radius: 4px;
  resize: vertical;
  background-color: ${({ theme }) => theme.forecast.background1};
  color: ${({ theme }) => theme.forecast.activeText};
`

const SaveButton = styled.button`
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.forecast.border};
  background-color: ${({ theme }) => theme.forecast.background1};
  color: ${({ theme }) => theme.forecast.activeText};
  font-family: sans-serif;
  width: fit-content;
  align-self: flex-end;

  &:hover {
    opacity: 0.8;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const ErrorMessage = styled.div`
  color: ${({ theme }) => theme.common.alert.error};
  font-size: 0.8em;
  margin-top: 4px;
`

export const PointCloudSettingsEditor: FC = () => {
  const { rolodexPatch } = useRolodexEdit()
  const { activePointCloud, setShowEditPointCloudConfigPopupMenu } =
    usePointCloudContext()
  const [jsonString, setJsonString] = useState('')
  const [isValidJson, setIsValidJson] = useState(true)
  const [parseError, setParseError] = useState<string | null>(null)

  useEffect(() => {
    if (activePointCloud) {
      const settingsToEdit = {
        name: activePointCloud.data.name ?? '',
        default_settings: activePointCloud.data.default_settings ?? {},
        site_model_orientation:
          activePointCloud.data.site_model_orientation ?? {},
      }
      setJsonString(JSON.stringify(settingsToEdit, null, 2))
      setIsValidJson(true)
      setParseError(null)
    }
  }, [activePointCloud])

  const handleJsonChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newJsonString = event.target.value
    setJsonString(newJsonString)
    try {
      JSON.parse(newJsonString)
      setIsValidJson(true)
      setParseError(null)
    } catch (error) {
      setIsValidJson(false)
      if (error instanceof Error) {
        setParseError(`Invalid JSON: ${error.message}`)
      } else {
        setParseError('Invalid JSON format.')
      }
    }
  }

  const handleSave = () => {
    if (!activePointCloud || !isValidJson) return

    try {
      const parsedSettings = JSON.parse(jsonString)

      if (typeof parsedSettings !== 'object' || parsedSettings === null) {
        throw new Error('JSON must be an object.')
      }
      const { name, default_settings, site_model_orientation } = parsedSettings
      rolodexPatch([
        {
          id: activePointCloud.id,
          data: {
            name: name || undefined,
            default_settings: default_settings ?? {},
            site_model_orientation: site_model_orientation ?? {},
          },
        },
      ])
      setShowEditPointCloudConfigPopupMenu(undefined)
      setParseError(null)
    } catch (error) {
      setIsValidJson(false)
      if (error instanceof Error) {
        setParseError(`Save Error: ${error.message}`)
      } else {
        setParseError('Error saving settings.')
      }
    }
  }

  if (!activePointCloud) {
    return <div>No active point cloud selected.</div>
  }

  return (
    <EditorContainer
      onMouseDown={(e) => e.stopPropagation()}
      onMouseUp={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
      onWheel={(e) => e.stopPropagation()}
      onContextMenu={(e) => e.stopPropagation()}>
      <label htmlFor='jsonEditor'>Edit Settings (JSON):</label>
      <JsonTextArea
        id='jsonEditor'
        value={jsonString}
        onChange={handleJsonChange}
        onKeyDown={(e) => e.stopPropagation()}
        aria-invalid={!isValidJson}
      />
      {parseError && <ErrorMessage>{parseError}</ErrorMessage>}
      <SaveButton onClick={handleSave} disabled={!isValidJson}>
        Save Settings
      </SaveButton>
    </EditorContainer>
  )
}
